import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { DsButtonDirective } from '../primitives/button.directive';
import { DsIconComponent } from '../primitives/icon.component';
import { DsProgressComponent } from '../primitives/surface.components';

@Component({
  selector: 'ds-assessment-instructions',
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-assessment-instructions' },
  template: `<div class="ds-assessment-instructions__icon"><ds-icon name="info" /></div>
    <div>
      <p class="ds-eyebrow">{{ eyebrow() }}</p>
      <h1>{{ title() }}</h1>
      <p>{{ summary() }}</p>
      <dl>
        @for (fact of facts(); track fact.label) {
          <div>
            <dt>{{ fact.label }}</dt>
            <dd>{{ fact.value }}</dd>
          </div>
        }
      </dl>
      <div class="ds-assessment-instructions__body"><ng-content /></div>
    </div>`,
})
export class DsAssessmentInstructionsComponent {
  readonly eyebrow = input('Assessment');
  readonly title = input.required<string>();
  readonly summary = input('');
  readonly facts = input<Array<{ label: string; value: string }>>([]);
}

@Component({
  selector: 'ds-question-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-question-card' },
  template: `<header>
      <p class="ds-eyebrow">{{ domain() }}</p>
      <span>{{ position() }} / {{ total() }}</span>
    </header>
    <h1 [id]="headingId()" tabindex="-1">{{ prompt() }}</h1>
    <div class="ds-question-card__visual"><ng-content select="[dsQuestionVisual]" /></div>
    <div class="ds-question-card__answers" role="radiogroup" [attr.aria-labelledby]="headingId()">
      <ng-content />
    </div>`,
})
export class DsQuestionCardComponent {
  readonly domain = input('');
  readonly position = input.required<number>();
  readonly total = input.required<number>();
  readonly prompt = input.required<string>();
  readonly headingId = input('assessment-question');
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);

  focusHeading(): void {
    this.element.nativeElement.querySelector<HTMLElement>('h1')?.focus();
  }
}

@Component({
  selector: 'ds-answer-option',
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-answer-option-host' },
  template: `<button
    type="button"
    class="ds-answer-option"
    role="radio"
    [attr.aria-checked]="selected()"
    [tabIndex]="selected() || tabStop() ? 0 : -1"
    [class.is-selected]="selected()"
    [disabled]="disabled()"
    (click)="chosen.emit()"
    (keydown)="onKeydown($event)"
  >
    <span class="ds-answer-option__key">{{ optionKey() }}</span
    ><span class="ds-answer-option__label">{{ label() }}</span
    ><ds-icon name="check" />
  </button>`,
})
export class DsAnswerOptionComponent {
  readonly optionKey = input.required<string>();
  readonly label = input.required<string>();
  readonly selected = input(false);
  readonly tabStop = input(false);
  readonly disabled = input(false);
  readonly chosen = output<void>();

  onKeydown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const current = event.currentTarget as HTMLButtonElement;
    const group = current.closest('[role="radiogroup"]');
    const options = Array.from(
      group?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? [],
    );
    if (!options.length) return;

    const currentIndex = Math.max(0, options.indexOf(current));
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? options.length - 1
          : (currentIndex +
              (['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1) +
              options.length) %
            options.length;

    event.preventDefault();
    options[nextIndex].focus();
    options[nextIndex].click();
  }
}

@Component({
  selector: 'ds-question-navigator',
  imports: [DsButtonDirective, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-question-navigator' },
  template: `<button
      dsButton
      variant="secondary"
      type="button"
      [disabled]="previousDisabled()"
      (click)="previous.emit()"
    >
      <ds-icon name="arrow-left" />{{ previousLabel() }}</button
    ><span aria-live="polite">{{ position() }} {{ ofLabel() }} {{ total() }}</span
    ><button dsButton type="button" [disabled]="nextDisabled()" (click)="next.emit()">
      {{ nextLabel() }}<ds-icon name="arrow-right" />
    </button>`,
})
export class DsQuestionNavigatorComponent {
  readonly position = input.required<number>();
  readonly total = input.required<number>();
  readonly previousDisabled = input(false);
  readonly nextDisabled = input(false);
  readonly previousLabel = input('Previous');
  readonly nextLabel = input('Next');
  readonly ofLabel = input('of');
  readonly previous = output<void>();
  readonly next = output<void>();
}

@Component({
  selector: 'ds-assessment-progress',
  imports: [DsProgressComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-assessment-progress' },
  template: `<ds-progress [value]="percentage()" [label]="label()" /><span
      >{{ answered() }} / {{ total() }}</span
    >`,
})
export class DsAssessmentProgressComponent {
  readonly answered = input.required<number>();
  readonly total = input.required<number>();
  readonly label = input('Assessment progress');
  protected readonly percentage = computed(() =>
    Math.round((this.answered() / Math.max(1, this.total())) * 100),
  );
}

@Component({
  selector: 'ds-assessment-timer',
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-assessment-timer', '[class.is-warning]': 'seconds() <= warningAt()' },
  template: `<ds-icon name="clock" /><span class="ds-visually-hidden">{{ label() }}</span
    ><strong>{{ formatted() }}</strong>`,
})
export class DsAssessmentTimerComponent {
  readonly seconds = input.required<number>();
  readonly warningAt = input(300);
  readonly label = input('Time remaining');
  protected readonly formatted = computed(
    () =>
      `${Math.floor(this.seconds() / 60)
        .toString()
        .padStart(2, '0')}:${(this.seconds() % 60).toString().padStart(2, '0')}`,
  );
}

@Component({
  selector: 'ds-review-flag',
  imports: [DsButtonDirective, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-review-flag' },
  template: `<button
    dsButton
    variant="quiet"
    type="button"
    [attr.aria-pressed]="flagged()"
    (click)="flaggedChange.emit(!flagged())"
  >
    <ds-icon name="flag" />{{ flagged() ? removeLabel() : addLabel() }}
  </button>`,
})
export class DsReviewFlagComponent {
  readonly flagged = input(false);
  readonly addLabel = input('Flag for review');
  readonly removeLabel = input('Remove review flag');
  readonly flaggedChange = output<boolean>();
}

@Component({
  selector: 'ds-result-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-result-card' },
  template: `<p class="ds-eyebrow">{{ eyebrow() }}</p>
    <h2>{{ title() }}</h2>
    <div class="ds-result-card__score">
      <strong>{{ score() }}</strong>
      @if (range()) {
        <span>{{ range() }}</span>
      }
    </div>
    <p>{{ summary() }}</p>
    <ng-content />`,
})
export class DsResultCardComponent {
  readonly eyebrow = input('Result');
  readonly title = input.required<string>();
  readonly score = input.required<string | number>();
  readonly range = input('');
  readonly summary = input('');
}

@Component({
  selector: 'ds-score-visualization',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-score-visualization' },
  template: `<svg viewBox="0 0 120 120" role="img" [attr.aria-label]="label()">
      <circle class="track" cx="60" cy="60" r="50" pathLength="100" />
      <circle
        class="value"
        cx="60"
        cy="60"
        r="50"
        pathLength="100"
        [attr.stroke-dasharray]="value() + ' ' + (100 - value())"
      /></svg
    ><strong>{{ displayValue() }}</strong
    ><span>{{ label() }}</span>`,
})
export class DsScoreVisualizationComponent {
  readonly value = input.required<number>();
  readonly displayValue = input.required<string | number>();
  readonly label = input('Score');
}

export interface DsAssessmentHistoryItem {
  id: string;
  title: string;
  date: string;
  status: string;
  score?: string;
}
@Component({
  selector: 'ds-assessment-history',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ds-assessment-history' },
  template: `<ul>
    @for (item of items(); track item.id) {
      <li>
        <div>
          <strong>{{ item.title }}</strong
          ><span>{{ item.date }}</span>
        </div>
        <div>
          <span class="ds-badge">{{ item.status }}</span>
          @if (item.score) {
            <strong>{{ item.score }}</strong>
          }
        </div>
      </li>
    } @empty {
      <li class="ds-assessment-history__empty">{{ emptyLabel() }}</li>
    }
  </ul>`,
})
export class DsAssessmentHistoryComponent {
  readonly items = input<DsAssessmentHistoryItem[]>([]);
  readonly emptyLabel = input('No assessment history yet.');
}
